import { Lock, Shield } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { getAccountDisplayName } from "@/helpers";
import { useConfigStore } from "@/stores/useConfigStore";
import { IAccount } from "@/types";

type LockedAccountPlaceholderProps = {
  account: IAccount;
  onUpgrade: () => void;
  variant?: "list" | "grid";
};

const ProBadge = () => (
  <div
    className="relative flex h-12 w-12 items-center justify-center rounded-xl"
    style={{
      background: "linear-gradient(135deg, #2563eb, #3b82f6, #60a5fa)",
      boxShadow: "0 0 20px rgba(37, 99, 235, 0.35), 0 0 6px rgba(37, 99, 235, 0.2)",
    }}
  >
    <Shield className="h-5.5 w-5.5 text-white" strokeWidth={2} />
  </div>
);

const FakeTaskRow = ({ width, dimmed }: { width: string; dimmed?: boolean }) => (
  <div className={`flex items-center gap-2.5 py-2 ${dimmed ? "opacity-60" : "opacity-90"}`}>
    <div className="h-3.5 w-3.5 rounded border-2 border-muted-foreground/30 shrink-0" />
    <div className="h-2.5 rounded-full bg-muted-foreground/25" style={{ width }} />
  </div>
);

export const LockedAccountPlaceholder = ({ account, onUpgrade, variant = "grid" }: LockedAccountPlaceholderProps) => {
  const accountNickname = useConfigStore((state) => state.config.accounts[account.id]?.nickname);

  return (
    <div className={variant === "list" ? "relative px-[25%]" : "w-fit relative"}>
      <div className="mb-4 flex gap-4 items-center">
        <Avatar className="h-6 w-6 ph-no-capture opacity-50">
          <AvatarImage src={account?.picture} />
          <AvatarFallback>{`${account?.name?.charAt(0)}`}</AvatarFallback>
        </Avatar>
        <span className="min-w-max ph-no-capture text-muted-foreground/70">
          {getAccountDisplayName(account, accountNickname)}
        </span>
        <Lock className="h-3.5 w-3.5 text-muted-foreground/50" />
      </div>

      <div className={`relative overflow-hidden rounded-xl ${variant === "list" ? "w-full" : "w-[300px]"}`}>
        {/* Peek-a-boo: faux task list visible behind the glass */}
        <div className="px-5 pt-5 pb-8 bg-card rounded-xl border border-border/50">
          {/* <div className="h-3 w-24 rounded-full bg-muted-foreground/25 mb-4" /> */}
          <FakeTaskRow width="70%" />
          <FakeTaskRow width="55%" />
          <FakeTaskRow width="80%" dimmed />
          <FakeTaskRow width="45%" dimmed />
          <FakeTaskRow width="65%" dimmed />
          <FakeTaskRow width="50%" dimmed />
        </div>

        {/* Glassmorphism overlay */}
        <div
          className="absolute inset-0 flex flex-col items-center justify-center gap-5 px-6 text-center rounded-xl"
          style={{
            background: "rgba(255, 255, 255, 0.03)",
            backdropFilter: "blur(4px)",
            WebkitBackdropFilter: "blur(4px)",
            border: "1px solid rgba(255, 255, 255, 0.08)",
            boxShadow: "0 8px 32px rgba(0, 0, 0, 0.25), inset 0 1px 0 rgba(255, 255, 255, 0.06)",
          }}
        >
          <ProBadge />

          <div className="space-y-2">
            <p className="text-sm font-semibold tracking-wide">
              Unlock Multi-Account Sync
            </p>
            <p className="text-xs text-muted-foreground/70 leading-relaxed max-w-[220px]">
              View tasks and lists from every connected account in one place.
            </p>
          </div>

          <button
            onClick={onUpgrade}
            className="relative px-5 py-1.5 text-sm font-medium rounded-lg text-white overflow-hidden transition-all hover:brightness-110 active:scale-[0.98]"
            style={{
              background: "linear-gradient(135deg, #2563eb, #3b82f6)",
              boxShadow: "0 2px 8px rgba(59, 130, 246, 0.35), 0 1px 2px rgba(0, 0, 0, 0.1)",
            }}
          >
            Upgrade to Pro
          </button>
        </div>
      </div>
    </div>
  );
};

export const LockedAccountSidebar = ({ account, onUpgrade }: LockedAccountPlaceholderProps) => {
  const accountNickname = useConfigStore((state) => state.config.accounts[account.id]?.nickname);

  return (
    <div className="py-4 px-4">
      <div className="flex items-center gap-2 mb-2.5">
        <Avatar className="h-5 w-5 ph-no-capture opacity-50">
          <AvatarImage src={account?.picture} />
          <AvatarFallback>{`${account?.name?.charAt(0)}`}</AvatarFallback>
        </Avatar>
        <span className="text-sm text-muted-foreground/70 truncate ph-no-capture">
          {getAccountDisplayName(account, accountNickname)}
        </span>
        <Lock className="h-3 w-3 text-muted-foreground/50 shrink-0" />
      </div>
      <button
        onClick={onUpgrade}
        className="flex items-center gap-1.5 text-xs font-medium transition-colors"
        style={{ color: "#3b82f6" }}
        onMouseEnter={(e) => (e.currentTarget.style.color = "#2563eb")}
        onMouseLeave={(e) => (e.currentTarget.style.color = "#3b82f6")}
      >
        <Shield className="h-3 w-3" />
        Unlock with Pro
      </button>
    </div>
  );
};
